import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import { getMachineConditionLabels, CRITICALITY_MAP } from 'common/constants';
import * as machineInfoActions from '../MachineInfo/actions/machineInfo.actions';
import AssetHierarchyPanel from './components/AssetHierarchyPanel';
import SensorsPanel from './components/SensorsPanel';
import { collectSensorsFromMachineInfo } from './utils/machineInfoSensors';
import {
  ShellRoot,
  Sidebar,
  SidebarBrand,
  SidebarSection,
  SidebarSectionTitle,
  SidebarScroll,
  MainColumn,
  TopBar,
  TopBarCluster,
  BreadcrumbText,
  BadgeRow,
  StatusPill,
  CriticalityPill,
  TabsSlot,
  ContentSlot,
} from './DigitalTwinMachineShell.styled';

function getConditionColors(conditionLabel) {
  const label = (conditionLabel || '').toLowerCase();
  if (label.includes('critical') || label.includes('danger') || label.includes('alarm')) {
    return { bg: '#FEE2E2', fg: '#991B1B' };
  }
  if (label.includes('warning') || label.includes('warn')) {
    return { bg: '#FEF3C7', fg: '#92400E' };
  }
  if (label.includes('good') || label.includes('ok') || label.includes('normal')) {
    return { bg: '#D1FAE5', fg: '#065F46' };
  }
  return { bg: '#F3F4F6', fg: '#374151' };
}

function buildBreadcrumbParts({ currentAccount, breadcrumb, machineName }) {
  const parts = [];
  if (currentAccount && currentAccount.name) {
    parts.push(currentAccount.name);
  }
  if (breadcrumb && breadcrumb.site && breadcrumb.site.name) {
    parts.push(breadcrumb.site.name);
  }
  if (breadcrumb && breadcrumb.area && breadcrumb.area.name) {
    parts.push(breadcrumb.area.name);
  }
  if (machineName) {
    parts.push(machineName);
  }
  return parts;
}

function DigitalTwinMachineShell(props) {
  const {
    machineId,
    partnerTitle,
    currentAccount,
    breadcrumb,
    hierarchy,
    machineInfoDetails,
    machineInfoLoading,
    getMachineInfoAction,
    children,
    assetConditionLevels,
    noContentPadding,
  } = props;
  const { t } = useTranslation();

  useEffect(() => {
    if (machineId) {
      getMachineInfoAction(machineId);
    }
  }, [machineId, getMachineInfoAction]);

  const machineName =
    (breadcrumb && breadcrumb.machine && breadcrumb.machine.name) ||
    (props.machineNameOverride || '');

  const parts = useMemo(
    () =>
      buildBreadcrumbParts({
        currentAccount,
        breadcrumb,
        machineName,
      }),
    [currentAccount, breadcrumb, machineName]
  );

  const sensors = useMemo(() => collectSensorsFromMachineInfo(machineInfoDetails), [machineInfoDetails]);

  const labels = getMachineConditionLabels(assetConditionLevels);
  const conditionLabel = formatConditionLabel(machineInfoDetails && machineInfoDetails.condition, labels, t);

  const critRaw =
    machineInfoDetails && machineInfoDetails.criticality != null
      ? CRITICALITY_MAP[machineInfoDetails.criticality]
      : 'High';
  const criticalityLabel = t('machineDashboard.digitalTwinExperience.criticalityLabel', {
    level: formatCriticalityDisplay(critRaw, t),
  });

  // Children must be ordered: [0] tab navigation, [1] route content.
  // Do not wrap children in fragments or conditional elements.
  if (process.env.NODE_ENV !== 'production' && React.Children.count(children) !== 2) {
    console.error('DigitalTwinMachineShell expects exactly 2 children: [tabs, routes]');
  }
  const childArray = React.Children.toArray(children);
  const tabsEl = childArray[0];
  const contentEl = childArray[1];

  return (
    <ShellRoot>
      <Sidebar>
        <SidebarBrand>
          {partnerTitle
            ? t('machineDashboard.digitalTwinExperience.productTitle', { partner: partnerTitle })
            : t('machineDashboard.digitalTwinExperience.productTitleFallback')}
        </SidebarBrand>
        <SidebarSection $flex>
          <SidebarSectionTitle>{t('machineDashboard.digitalTwinExperience.assetHierarchy')}</SidebarSectionTitle>
          <SidebarScroll>
            <AssetHierarchyPanel hierarchy={hierarchy} machineId={machineId} />
          </SidebarScroll>
        </SidebarSection>
        <SidebarSection>
          <SidebarSectionTitle>{t('machineDashboard.digitalTwinExperience.sensors')}</SidebarSectionTitle>
          <SidebarScroll>
            <SensorsPanel sensors={sensors} loading={machineInfoLoading} />
          </SidebarScroll>
        </SidebarSection>
      </Sidebar>
      <MainColumn>
        <TopBar>
          <TopBarCluster>
            <BreadcrumbText>
              {parts.map((p, i) => (
                <span key={`${p}-${i}`}>
                  {i > 0 && ' > '}
                  {i === parts.length - 1 ? <strong>{p}</strong> : <span>{p}</span>}
                </span>
              ))}
            </BreadcrumbText>
            <BadgeRow>
              <StatusPill $bg={getConditionColors(conditionLabel).bg} $fg={getConditionColors(conditionLabel).fg}>
                {conditionLabel}
              </StatusPill>
              <CriticalityPill>{criticalityLabel}</CriticalityPill>
            </BadgeRow>
          </TopBarCluster>
        </TopBar>
        <TabsSlot>{tabsEl}</TabsSlot>
        <ContentSlot $noPadding={noContentPadding} $flexFill={noContentPadding}>{contentEl}</ContentSlot>
      </MainColumn>
    </ShellRoot>
  );
}

function formatConditionLabel(condition, labels, t) {
  if (condition == null || condition === '') {
    return labels[1] || t('condition.warning');
  }
  const s = String(condition).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatCriticalityDisplay(criticalityRaw, t) {
  const criticalityKeyMap = {
    'Very High': 'veryHigh',
    High: 'high',
    Medium: 'medium',
    Low: 'low',
    'Very Low': 'veryLow',
  };
  const key = criticalityKeyMap[criticalityRaw];
  if (key) return t(`machineDashboard.widgets.criticality.${key}`);
  return criticalityRaw || '—';
}

DigitalTwinMachineShell.propTypes = {
  machineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  partnerTitle: PropTypes.string,
  currentAccount: PropTypes.object,
  breadcrumb: PropTypes.object,
  hierarchy: PropTypes.array,
  machineInfoDetails: PropTypes.object,
  machineInfoLoading: PropTypes.bool,
  getMachineInfoAction: PropTypes.func.isRequired,
  children: PropTypes.node,
  assetConditionLevels: PropTypes.number,
  machineNameOverride: PropTypes.string,
  noContentPadding: PropTypes.bool,
};

DigitalTwinMachineShell.defaultProps = {
  children: null,
  partnerTitle: '',
  currentAccount: null,
  breadcrumb: null,
  hierarchy: null,
  machineInfoDetails: null,
  machineInfoLoading: false,
  assetConditionLevels: 4,
  machineNameOverride: '',
  noContentPadding: false,
};

const mapStateToProps = (state, ownProps) => {
  const md = state.machineDetails.details && state.machineDetails.details.object;
  const machineId = ownProps.machineId || (md && md.id);
  return {
    machineId,
    partnerTitle: state.companyReducer.partner.title,
    currentAccount: state.currentAccount,
    breadcrumb: state.breadcrumb,
    hierarchy: state.assetHierarchyReducer.assetInfo.hierarchy,
    machineInfoDetails: state.machineDetails.info.details,
    machineInfoLoading: !!(state.machineDetails.info && state.machineDetails.info.loadingDetails),
    assetConditionLevels: state.currentAccount.preferences.machine_condition_levels,
    machineNameOverride: md && md.name,
  };
};

const mapDispatchToProps = dispatch => ({
  getMachineInfoAction: bindActionCreators(machineInfoActions.getMachineInfo, dispatch),
});

// Unconnected component exported under a distinct name so tests can mount it
// with plain props and no store. The `Base` suffix follows the convention in
// OrgSiteBreadcrumb.js and keeps import/no-named-as-default quiet at the
// default-import sites.
export { DigitalTwinMachineShell as DigitalTwinMachineShellBase };

export default connect(mapStateToProps, mapDispatchToProps)(DigitalTwinMachineShell);
